import { Box, Center, Flex, Spinner, Switch, Text, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { useDisclosure } from "@equidefi/hooks";
import { extractErrorMessage } from "@equidefi/portals";
import { VaultContext } from "@equidefi/portals/context/VaultProvider";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useGetInquiry } from "@equidefi/portals/hooks/useInquiries";
import { BannerAlert, Modal } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { InvestmentStatusView } from "@/components/offerings/StatusView";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { CONFIG } from "@/constants/config";
import { useStartInvestmentInquiry } from "@/hooks/useInquiries";
import { useLockHistory } from "@/hooks/useLockHistory";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import PersonaLogo from "@/images/persona.svg";
import { INQUIRY_PENDING_STATUSES, canBeSilent } from "@equidefi/shared/constants/inquiries";
import { WorkflowContainer } from "../WorkflowContainer";
import { ReviewInvestment } from "./ReviewInvestment";

const DebugView = ({ vault, passInquiry, setPassInquiry }) => {
  const [hide, setHide] = useState(false);

  // Enable this when Persona enables simulation via API
  /* const DebugOptions = [
    { label: "AAMVA", value: "AAMVA_FAILED" },
    { label: "Adverse Media", value: "ADVERSE_MEDIA_MATCH" },
    { label: "Database", value: "DATABASE_FAILED" },
    { label: "PEP", value: "PEP_MATCH" },
    { label: "Watchlist", value: "WATCHLIST_MATCH" },
  ]; */

  if (
    hide ||
    CONFIG.ENV === "production" ||
    !canBeSilent({
      entityType: vault?.entity_type,
      citizenship: vault?.citizenship,
    })
  ) {
    return null;
  }

  return (
    <Box
      position="fixed"
      bottom={5}
      left={5}
      backgroundColor="#FFFFE0"
      // width={200}
      // height={300}
      zIndex={100000}
      borderRadius={10}
      margin={0}
      padding="0 25px 25px 25px">
      <Box position="absolute" top={0} right={0} pr={3} pt={3} cursor="pointer" onClick={() => setHide(true)}>
        X
      </Box>
      <Text fontWeight="bold" textStyle="h2" mb="0" mt={6}>
        Persona Debugger
      </Text>
      <Text mb="2.5" flexWrap="wrap" maxWidth={250} fontSize="sm">
        Select a report or verification to fail, if non-selected inquiry will be successful. Only available in
        non-production environments.
      </Text>
      <Flex position="flex" flexDir="row" mt={5}>
        <Switch isChecked={passInquiry} onChange={() => setPassInquiry(!passInquiry)} colorScheme="green" />
        <Text mb="2.5" fontSize="sm" ml={3}>
          Pass Inquiry
        </Text>
      </Flex>
    </Box>
  );
};

const InquiryStatusView = ({ isLoading: _isLoading, kyckyb, passInquiry }) => {
  const { investment, offering, refetchInvestment, user } = useWorkflowContext();
  const {
    data: inquiry,
    isFetching: isFetchingInquiry,
    refetch: refetchInquiry,
  } = useGetInquiry(investment?.vault?.inquiry_id, {
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  });

  const toast = useToast();
  useDocumentTitle(["Verify Your Identity", offering?.name, investment?.issuer?.name]);

  useLockHistory();

  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const processingModal = useDisclosure();
  const { onStart, isLoading: isStartLoading } = useStartInvestmentInquiry(investment?.id, {
    onComplete: async () => {
      processingModal.onClose();
      navigate(`/offerings/${offering.slug}/${investment.id}/agreement`, {
        replace: true,
      });
    },
    onCancel: async () => {
      processingModal.onClose();
      await refetchInvestment();
    },
    onReady: () => {
      processingModal.onClose();
    },
  });

  const inquiryStatus = useMemo(
    () => inquiry?.status ?? investment?.vault?.inquiry?.status ?? "unknown",
    [inquiry?.status, investment],
  );

  const isRestricted = useMemo(
    () => inquiryStatus !== "unknown" && !INQUIRY_PENDING_STATUSES.includes(inquiryStatus),
    [inquiryStatus],
  );

  useEffect(() => {
    refetchInvestment();
  }, [refetchInvestment]);

  useEffect(() => {
    if (investment?.vault?.inquiry_id) {
      refetchInquiry();
    }
  }, [refetchInquiry, investment]);

  const onSubmit = async () => {
    // Prevent double firing onClick
    if (isLoading) {
      return;
    }

    setStarted(true);

    let debug;
    if (CONFIG.ENV !== "production") {
      debug = {
        pass_inquiry: passInquiry,
      };
    }

    if (inquiryStatus !== "unknown") {
      window.location.replace(`/offerings/${offering.slug}/${investment.id}/agreement`);
      return;
    }

    try {
      // Show processing modal immediately after click
      processingModal.onOpen();

      await onStart(investment.user_id, investment.vault.entity_type, investment.vault.citizenship, debug);
    } catch (error) {
      console.error(error);
      processingModal.onClose();
      toast({
        status: "error",
        description: extractErrorMessage(error),
      });
    } finally {
      await refetchInvestment();
      setStarted(false);
    }
  };

  const isLoading = isStartLoading || _isLoading || started || isFetchingInquiry;

  if (!inquiryStatus || !investment) {
    return null;
  }

  if (isRestricted) {
    return (
      <>
        <InvestmentStatusView title={`${kyckyb === "KYB" ? "Business verification" : "Identity check"} submitted...`}>
          Your {kyckyb === "KYB" ? "business verification" : "identity check"} has been submitted and is processing,
          please click the link below to continue your investment.
        </InvestmentStatusView>
        <WorkflowButton
          isLoading={isLoading}
          onClick={() => navigate(`/offerings/${offering.slug}/${investment.id}/agreement`)}>
          Continue
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "unknown") {
    return (
      <>
        <Modal
          isOpen={processingModal.isOpen}
          closeOnOverlayClick={false}
          closeOnEsc={false}
          contentProps={{
            maxW: { base: "90%", md: "440px" },
            sx: {
              "button[aria-label='Close']": {
                display: "none",
              },
            },
          }}
          Header={
            <Text textStyle="h2" fontWeight="bold">
              Processing Your Information
            </Text>
          }>
          <Text textStyle="body1" color="gray.700" mb={6}>
            We're reviewing your submitted information and preparing your subscription agreement.
          </Text>
          <Center mb={6}>
            <Spinner size="xl" color="equidefi.primary" thickness="4px" speed="0.75s" />
          </Center>
          <BannerAlert
            status="warning"
            title="Please Wait"
            description="This might take a few seconds. Please do not refresh or close your browser, or navigate away from this page."
          />
        </Modal>
        <InvestmentStatusView title="Review Your Investment" showPersonaLogo={false}>
          <BannerAlert
            status="warning"
            title="Verify Your Information"
            description="Please review the below information carefully and ensure it is correct."
            my={6}
          />
          <ReviewInvestment />
        </InvestmentStatusView>
        <BannerAlert
          status="warning"
          title="No Changes Beyond This Point"
          description="You will not be able to make any changes to your investment after clicking the button below. Please ensure all information on this page is correct before proceeding."
          mb={6}
        />
        <WorkflowButton isLoading={isLoading} onClick={onSubmit}>
          Start
        </WorkflowButton>
        <Center mt="4" w="full">
          <Box
            as="object"
            data={PersonaLogo}
            type="image/svg+xml"
            aria-label="Powered by Persona"
            maxW="360px"
            alignSelf="center"
            data-aos="fade-up"
            data-aos-delay="100"
          />
        </Center>
      </>
    );
  }

  if (inquiryStatus === "approved") {
    return (
      <>
        <InvestmentStatusView title="Approved!" icon={Icon.CheckCircle}>
          Well done! It looks like your {kyckyb === "KYB" ? "business verification" : "individual identity"} check was
          approved. You can continue to proceed with our investment.
        </InvestmentStatusView>
        <WorkflowButton isLoading={isLoading} as={Link} to={`/offerings/${offering.slug}/${investment.id}/agreement`}>
          Continue
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "completed") {
    if (kyckyb === "KYB")
      return (
        <>
          <InvestmentStatusView title="Business verification started...">
            You have been sent an email with a request to complete your individual identity check. Your business
            verification has been started, and you will be notified by email once your business verification check has
            been reviewed.
          </InvestmentStatusView>
          <WorkflowButton isLoading={isLoading} as={Link} to={`/offerings/${offering.slug}/${investment.id}/agreement`}>
            Continue
          </WorkflowButton>
        </>
      );

    if (kyckyb === "KYC")
      return (
        <>
          <InvestmentStatusView title="Processing...">
            Your identity check has been completed, but is still processing. Please standby...
          </InvestmentStatusView>
          <Center>
            <Spinner />
          </Center>
        </>
      );
  }

  if (inquiryStatus === "created" || inquiryStatus === "pending" || isRestricted) {
    if (
      canBeSilent({
        citizenship: investment.vault.citizenship,
        entityType: investment.vault.entity_type,
      })
    ) {
      return (
        <>
          <InvestmentStatusView title="Approved!" icon={Icon.CheckCircle}>
            Well done! It looks like your individual identity check was started. You can continue to proceed with our
            investment.
          </InvestmentStatusView>
          <WorkflowButton isLoading={isLoading} as={Link} to={`/offerings/${offering.slug}/${investment.id}/agreement`}>
            Continue
          </WorkflowButton>
        </>
      );
    } else {
      return (
        <>
          <InvestmentStatusView title={`${kyckyb === "KYB" ? "Business verification" : "Identity check"} started...`}>
            Your {kyckyb === "KYB" ? "business verification" : "identity check"} has been started but has not been
            completed yet, please click the link below to resume.
          </InvestmentStatusView>
          <WorkflowButton
            isLoading={isLoading}
            onClick={() => onStart(investment.user_id, investment.vault?.entity_type, investment.vault?.citizenship)}>
            Resume
          </WorkflowButton>
        </>
      );
    }
  }

  if (inquiryStatus === "expired" || inquiryStatus === "resumed") {
    return (
      <>
        <InvestmentStatusView
          title={`${kyckyb === "KYB" ? "Business verification" : "Identity verification"} expired`}
          centered
          muted>
          Your {kyckyb === "KYB" ? "business verification" : "identity verification"} has expired, please click the link
          below to resume.
        </InvestmentStatusView>
        <WorkflowButton
          isLoading={isLoading}
          onClick={() => onStart(investment.user_id, investment.vault?.entity_type, investment.vault?.citizenship)}>
          Resume
        </WorkflowButton>
      </>
    );
  }

  if (inquiryStatus === "declined") {
    return (
      <InvestmentStatusView title="Sorry!" icon={Icon.ShieldOff}>
        It looks like your {kyckyb === "KYB" ? "business verification" : "identity verification"} was declined. If you
        believe this to be in error, please contact the issuer of the offering.
      </InvestmentStatusView>
    );
  }

  if (inquiryStatus === "needs_review") {
    return (
      <InvestmentStatusView title="Under Review" icon={Icon.Activity}>
        It looks like your {kyckyb === "KYB" ? "business verification" : "identity verification"} was automatically
        marked for review. The issuer will review your information. You will receive an email when the issuer has
        accepted or declined your information.
      </InvestmentStatusView>
    );
  }
  return null;
};

export const WorkflowKYC = () => {
  const { investment, refetchInvestment } = useWorkflowContext();
  useWorkflowPosition(6);
  const [passInquiry, setPassInquiry] = useState(true);
  const { vault, refetch: refetchVault } = useContext(VaultContext);
  const location = useLocation();

  useEffect(() => {
    refetchVault();
  }, [location.state]);

  useEffect(() => {
    if (location.state?.refresh) {
      refetchInvestment();
    }
  }, []);

  const kyckyb = investment?.entity_type === "ENTITY" ? "KYB" : "KYC";

  return (
    <WorkflowContainer>
      <DebugView vault={vault} passInquiry={passInquiry} setPassInquiry={setPassInquiry} />
      <InquiryStatusView investment={investment} passInquiry={passInquiry} vault={vault} kyckyb={kyckyb} />
    </WorkflowContainer>
  );
};
